import { isAxiosError } from "axios";
import { useGlobalSearchParams } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Linking } from "react-native";

import { useGetClinicStatusForPatient } from "@/api/generated";
import { UniversalClinicVisitStatus } from "@/api/generated/models";
import { useLinkClassicAccount } from "@/api/mutations";
import { useMergeStatus } from "@/api/queries";
import type { MergePatientFacingStatus } from "@/api/queries/mergeStatus";
import {
  BulletedList,
  Button,
  FixedNumericInput,
  H3,
  Header,
  Heading,
  Link,
  LoadingSpinner,
  Paragraph,
  Section,
  Separator,
  Text,
  View,
  YStack,
  useMedia,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { CLASSIC_BASE_URL } from "@/config/env";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";

const CODE_LENGTH = 6;

// Deep-link back into Curai Classic's account page, where the merge code lives.
const CLASSIC_ACCOUNT_URL = `${CLASSIC_BASE_URL}/account`;

const SUPPORT_URL = "https://www.curaihealth.com/support";

type MergeScreenState =
  | "entry"
  | "in_progress"
  | "merged"
  | "failed"
  | "open_visit";

// Map a link-classic-account failure to inline copy. 400/404 both mean the code
// was rejected; 429 is rate-limiting; anything else is a generic failure.
function getMergeErrorText(error: unknown): string | undefined {
  if (!error) return undefined;
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === 400 || status === 404) {
    return "That code is invalid or expired. Please double-check and try again.";
  }
  if (status === 429) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  return "Something went wrong. Please try again.";
}

// Which screen to show. The merge runs asynchronously after a successful link, so
// a link success moves us to "in_progress" and the poll's patient-facing status
// takes it to the terminal "merged"/"failed".
function getMergeScreenState({
  isPending,
  linked,
  patientFacingStatus,
  hasOpenVisit,
}: {
  isPending: boolean;
  linked: boolean;
  patientFacingStatus: MergePatientFacingStatus | null | undefined;
  hasOpenVisit: boolean;
}): MergeScreenState {
  if (patientFacingStatus === "merged") return "merged";
  if (patientFacingStatus === "failed") return "failed";
  if (patientFacingStatus === "in_progress" || isPending || linked) {
    return "in_progress";
  }
  // No existing merge: an open (in-progress) visit on this CHAI account blocks a
  // new merge — the patient must finish the visit here first. This is checked
  // before the code auto-submits, so a carried deep-link never starts a merge
  // while a visit is open.
  if (hasOpenVisit) return "open_visit";
  return "entry";
}

function MergeInProgressView() {
  return (
    <YStack
      alignItems="flex-start"
      gap="$200"
      paddingVertical="$400"
      testID="merge-accounts-in-progress"
    >
      <LoadingSpinner size={56} />
      <Text size="$md" color="$textSecondary" textAlign="left">
        Migration in progress — it will take some time.
      </Text>
      <Text size="$sm" color="$textSecondary" textAlign="left">
        <Link href="/" dataTrackingId="merge-accounts-in-progress-home">
          Click here
        </Link>{" "}
        to go to the homepage and check out the new and improved Curai Health!
      </Text>
    </YStack>
  );
}

function MergeMergedView() {
  const router = usePropagatedRouter();

  return (
    <YStack
      alignItems="flex-start"
      gap="$200"
      paddingVertical="$400"
      testID="merge-accounts-merged"
    >
      <Text size="$md" color="$textTitle" textAlign="left">
        You're all set!
      </Text>
      <Text size="$sm" color="$textSecondary" textAlign="left">
        Your accounts are merged.
      </Text>
      <BulletedList
        items={[
          "Your prescriptions and orders will show in the homepage sidebar after your first visit.",
          "Your care team can see your full chart, so you can pick up where you left off.",
          "Previous chats won't come with you. Start a new visit for any open questions.",
        ]}
        icon={<BulletedList.Bullet />}
        size="$sm"
        color="$textSecondary"
      />
      <Text size="$sm" color="$textSecondary" textAlign="left">
        Bookmark{" "}
        <Link
          size="$sm"
          bold
          href="/"
          dataTrackingId="merge-accounts-merged-home"
        >
          app.curaihealth.com
        </Link>{" "}
        to get care anytime. Your free access runs through the end of the year.
      </Text>
      <Button
        mode="primary"
        onPress={() => router.navigate("/")}
        dataTrackingId="merge-accounts-merged-start-chat"
        alignSelf="stretch"
      >
        Start a chat now
      </Button>
    </YStack>
  );
}

function MergeFailedView() {
  return (
    <YStack
      alignItems="flex-start"
      gap="$200"
      paddingVertical="$400"
      testID="merge-accounts-failed"
    >
      <Text size="$md" color="$feedbackError" textAlign="left">
        Something went wrong...
      </Text>
      <Text size="$sm" color="$textSecondary" textAlign="left">
        Something went wrong while trying to merge your account. Don't worry,
        your care history is safe.
      </Text>
      <Text size="$sm" color="$textSecondary" textAlign="left">
        Our team has been notified and will work on migrating your account
        manually. You can also contact support at{" "}
        <Link size="$sm" bold href="mailto:support@curai.com" external>
          support@curai.com
        </Link>{" "}
        for a status update.
      </Text>
      <Button
        mode="primary"
        onPress={() => Linking.openURL("https://www.curaihealth.com/support")}
        dataTrackingId="merge-accounts-failed-support"
        minWidth="$2000"
        alignSelf="center"
      >
        Contact support
      </Button>
    </YStack>
  );
}

function MergeOpenVisitView() {
  return (
    <YStack
      gap="$200"
      paddingVertical="$200"
      testID="merge-accounts-open-visit"
    >
      <Heading size="$md" color="$textTitle">
        You have an open visit...
      </Heading>
      <Paragraph size="$sm" color="$textSecondary">
        You currently have an open visit on this account. You will need to
        complete the visit here, in this account, with your provider before
        migrating your old account.
      </Paragraph>
      <Paragraph size="$sm" color="$textSecondary">
        Once your visit is complete here, go to Settings in the old Curai Health
        app and retry the migration again.
      </Paragraph>
      <Paragraph size="$sm" color="$textSecondary">
        You can always contact support if you need any help!
      </Paragraph>
      <Link href={SUPPORT_URL} external>
        <Button mode="primary" testID="merge-accounts-open-visit-support">
          Contact support
        </Button>
      </Link>
    </YStack>
  );
}

function MergeEntryView({
  code,
  errorText,
  onChangeValue,
  onSubmit,
}: {
  code: string;
  errorText: string | undefined;
  onChangeValue: (value: string) => void;
  onSubmit: (value: string) => void;
}) {
  return (
    <YStack gap="$200">
      <Paragraph color="$textSecondary" size="$sm">
        Enter the verification code found on the settings page of the Curai
        mobile app.
      </Paragraph>

      <View testID="merge-accounts-input">
        <FixedNumericInput
          value={code}
          onChangeValue={onChangeValue}
          onSubmit={onSubmit}
          borderColor={errorText ? "$red" : "$strokeNeutral"}
        />
      </View>

      {errorText ? (
        <Text
          size="$sm"
          color="$red"
          role="alert"
          testID="merge-accounts-error"
        >
          {errorText}
        </Text>
      ) : null}

      <Text size="$sm" color="$textSecondary">
        Don't have a code?{" "}
        <Link
          href={CLASSIC_ACCOUNT_URL}
          external
          dataTrackingId="merge-accounts-open-classic"
        >
          Click here.
        </Link>
      </Text>

      <Button
        mode="primary"
        onPress={() => onSubmit(code)}
        disabled={code.trim().length !== CODE_LENGTH}
        testID="merge-accounts-submit"
        dataTrackingId="merge-accounts-submit-button"
      >
        Merge accounts
      </Button>

      {/* TODO: confirm exact wording */}
      <Text size="$xs" color="$textSecondary">
        If you were a member of Curai before Jan 1, 2026 on the native app, this
        applies to you.
      </Text>
    </YStack>
  );
}

/**
 * "Merge accounts" settings tab.
 *
 * Reached via `?settings=merge-accounts` — typically from Curai Classic's
 * deep-link into `/?settings=merge-accounts&mergeToken=<code>` (see the classic
 * app's `openMergeDeepLink`). The settings dialog provides the tab chrome, so
 * this only renders the tab body, which has four states:
 *  - entry:       manual 6-digit code entry ("code didn't carry")
 *  - in-progress: the merge was submitted; because merging is async (a few
 *                 minutes) we surface a "migration in progress" message and poll
 *                 `GET /user/merge-status` for the outcome
 *  - merged:      the poll reported the merge completed
 *  - failed:      the poll reported the merge failed (contact support)
 *  - open-visit:  the account has an open (in-progress) visit; the merge is
 *                 blocked until the visit is finished here (contact support)
 *
 * When the deep-link carries a `mergeToken` the input is prefilled and the merge
 * is auto-submitted — unless the account has an open visit, in which case we fork
 * to the open-visit screen and never auto-submit.
 */
function MergeAccountsTab() {
  const { lg } = useMedia();
  const { mergeToken } = useGlobalSearchParams<{ mergeToken?: string }>();

  const [code, setCode] = useState(mergeToken ?? "");
  const autoSubmittedRef = useRef(false);

  const {
    mutate: linkClassicAccount,
    data,
    isPending,
    isSuccess,
    error,
    reset,
  } = useLinkClassicAccount();

  // Poll merge status on mount (not only after linking) so a returning user with
  // an existing run sees its progress, and so we never start a second merge for a
  // patient who already has one. status "none" means no attempt exists yet.
  const linked = isSuccess && Boolean(data?.linked);
  // Fetch once on mount; only keep polling while a link is in-flight/just done
  // (the query itself also polls whenever the server reports in_progress).
  const { data: mergeStatus, isLoading: isStatusLoading } = useMergeStatus({
    enabled: true,
    poll: isPending || linked,
  });
  const hasExistingMerge = Boolean(
    mergeStatus && mergeStatus.status !== "none",
  );

  // Block a new merge while this CHAI account has an open (in-progress) visit —
  // the patient must finish it here first. Checked before auto-submit so a
  // carried deep-link can't kick off a merge behind an open visit.
  const { data: visitStatus, isLoading: isVisitStatusLoading } =
    useGetClinicStatusForPatient({
      query: { select: (statusData) => statusData.latest_visit?.status },
    });
  const hasOpenVisit =
    visitStatus === UniversalClinicVisitStatus["in-progress"];

  // Single chokepoint for both manual submit (button / input) and the auto-submit
  // effect below: never start a merge until we know the account's merge + visit
  // state, and never behind an existing merge or an open visit. Guarding here (not
  // just in the effect) closes the race where the entry form is shown while the
  // clinic visit status is still loading.
  const handleMerge = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed.length !== CODE_LENGTH || isPending) return;
      if (isStatusLoading || isVisitStatusLoading) return;
      if (hasExistingMerge || hasOpenVisit) return;
      linkClassicAccount({ code: trimmed });
    },
    [
      isPending,
      isStatusLoading,
      isVisitStatusLoading,
      hasExistingMerge,
      hasOpenVisit,
      linkClassicAccount,
    ],
  );

  // "Code carried": the deep-link supplied a merge code — prefill and auto-submit,
  // but only once we know the patient has no existing merge (status "none") and no
  // open visit. This gates on /merge-status and the clinic visit status so we
  // never kick off a run for an already-merged/in-progress patient, or one who
  // needs to finish a visit first.
  useEffect(() => {
    if (autoSubmittedRef.current) return;
    if (isStatusLoading || isVisitStatusLoading) return;
    if (hasExistingMerge || hasOpenVisit) return;
    if (mergeToken?.trim().length === CODE_LENGTH) {
      autoSubmittedRef.current = true;
      setCode(mergeToken);
      handleMerge(mergeToken);
    }
  }, [
    mergeToken,
    handleMerge,
    isStatusLoading,
    isVisitStatusLoading,
    hasExistingMerge,
    hasOpenVisit,
  ]);

  const screenState = getMergeScreenState({
    isPending,
    linked,
    patientFacingStatus: mergeStatus?.patient_facing_status,
    hasOpenVisit,
  });

  const views: Record<MergeScreenState, React.ReactNode> = {
    merged: <MergeMergedView />,
    failed: <MergeFailedView />,
    in_progress: <MergeInProgressView />,
    open_visit: <MergeOpenVisitView />,
    entry: (
      <MergeEntryView
        code={code}
        errorText={getMergeErrorText(error)}
        onChangeValue={(value) => {
          setCode(value);
          if (error) reset();
        }}
        onSubmit={handleMerge}
      />
    ),
  };

  return (
    <Section margin="$screenPadding" flex={1} $lg={{ flex: 1, gap: "$250" }}>
      {lg && (
        <Header gap="$250">
          <H3>Merge your old Curai account</H3>
          <Separator borderColor="$fillNeutral" />
        </Header>
      )}

      {views[screenState]}
    </Section>
  );
}

/**
 * Feature gate for the account-merge tab.
 *
 * This is the gate that matters, not the tab button in the settings nav: the
 * dialog renders the panel straight out of its tab map, so
 * `?settings=merge-accounts&mergeToken=<code>` mounts this pane directly and
 * auto-submits the merge. Not mounting the body is what makes that deep-link
 * inert while the flow is dark. Fails closed — a flag still loading renders
 * nothing.
 */
export default function MergeAccountsSettingsScreen() {
  const { value: isAccountMergeEnabled, loading } = useFeatureFlag(
    "curai-classic-to-chai-account-merge",
    false,
  );

  if (loading || !isAccountMergeEnabled) {
    return null;
  }

  return <MergeAccountsTab />;
}
